import type { Table as DataTableInstance } from "@tanstack/react-table";
import React from "react";
import { useIntl } from "react-intl";

import { wpxTableMessages } from "./wpxTable.intl";

import Paginator from "@/components/pagination/Paginator";

interface WpxTablePaginationControlsProps<TData> {
  currentPage?: number;
  handlePageChange: (
    pageNumber: number,
    setClientPageIndex?: (pageIndex: number) => void,
  ) => void;
  handlePageSizeChange?: (pageSize: number) => void;
  isLoading: boolean;
  isTablePaginated: boolean;
  pageSize: number;
  shouldRenderPaginator: boolean;
  showSelectedCount: boolean;
  showTotalCount: boolean;
  table: DataTableInstance<TData>;
  totalPages?: number;
  totalResultCount: number;
  visibleResultCount: number;
}

/**
 * Independent WPX pagination controls that can be composed outside `WpxTable`.
 *
 * @param {WpxTablePaginationControlsProps<TData>} props - Pagination controls props.
 * @returns {React.JSX.Element | null} Rendered pagination controls.
 */
const WpxTablePaginationControls = <TData,>(
  props: WpxTablePaginationControlsProps<TData>,
): React.JSX.Element | null => {
  const {
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    isLoading,
    isTablePaginated,
    pageSize,
    shouldRenderPaginator,
    showSelectedCount,
    showTotalCount,
    table,
    totalPages,
    totalResultCount,
    visibleResultCount,
  } = props;
  const intl = useIntl();

  if (!isTablePaginated || isLoading) {
    return null;
  }

  return (
    <div className='flex items-center py-4'>
      <div className='flex items-center gap-x-4'>
        {showSelectedCount &&
          (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
            <div className='text-sm whitespace-nowrap text-muted-foreground'>
              {intl.formatMessage(wpxTableMessages.rowsSelected, {
                selected: table.getFilteredSelectedRowModel().rows.length,
                total: table.getFilteredRowModel().rows.length,
              })}
            </div>
          )}

        {showTotalCount && totalResultCount > 0 && (
          <div className='ml-2 text-xs text-muted-foreground'>
            {intl.formatMessage(wpxTableMessages.showingTotalResults, {
              count: totalResultCount,
              visible: visibleResultCount,
              strong: (chunks: React.ReactNode) => (
                <span className='font-medium text-foreground'>{chunks}</span>
              ),
            })}
          </div>
        )}
      </div>
      <div className='ml-auto'>
        {shouldRenderPaginator && (
          <Paginator
            currentPage={
              currentPage ?? table.getState().pagination.pageIndex + 1
            }
            totalPages={totalPages ?? table.getPageCount()}
            pageSize={pageSize}
            onPageChange={(pageNumber) => {
              handlePageChange(pageNumber, table.setPageIndex);
            }}
            onPageSizeChange={handlePageSizeChange}
            showPreviousNext
          />
        )}
      </div>
    </div>
  );
};

export default WpxTablePaginationControls;
export type { WpxTablePaginationControlsProps };
